<template>
  <base-slide-over ref="slideover" :title="title" size="5xl">
    <div class="tw-px-4 sm:tw-px-6 tw-mt-4" v-if="isLoadingMappings">
      <div class="tw-flex tw-my-8 tw-items-center" v-for="i in 3" :key="i">
        <div class="tw-w-1/2 tw-flex tw-items-center tw-justify-between">
          <div class="tw-flex tw-items-center">
            <base-skeleton-loader class="tw-w-3 tw-h-3" />
            <base-skeleton-loader class="tw-ml-3 tw-h-3 tw-w-56" />
          </div>
          <arrow-right-icon class="tw-w-5 tw-h-5 tw-text-gray-500 tw-ml-2 tw-mr-4" />
        </div>
        <base-skeleton-loader class="tw-flex-grow tw-h-3" />
      </div>
    </div>
    <div class="tw-px-4 sm:tw-px-6 tw-mt-4" v-else>
      <div v-for="program in orderedPrograms" :key="program.businessProgramId" class="tw-flex tw-items-center tw-mb-6">
        <div class="tw-w-1/2 tw-flex tw-items-center tw-justify-between">
          <div class="tw-flex-col">
            <base-checkbox :value="program.businessProgramId" v-model="selectedPrograms">
              <div class="tw-flex-col tw-my-auto">
                {{ program.name }}
                <div class="tw-text-gray-500 tw-text-xs tw-font-normal tw-mt-1">{{ program.programGroup }}</div>
              </div>
            </base-checkbox>
          </div>
          <arrow-right-icon class="tw-w-5 tw-h-5 tw-text-gray-500 tw-ml-2 tw-mr-4" />
        </div>
        <base-select
          class="tw-flex-grow"
          :options="orderedPlans"
          label-key="name"
          value-key="businessPlanId"
          placeholder="Select a plan"
          :value="mappings[program.businessProgramId]"
          @input="handleInput(program.businessProgramId, $event)"
        />
      </div>
    </div>
    <template #fixed-bottom>
      <div class="tw-py-2 tw-px-3 tw-flex tw-justify-between">
        <base-button type="flat" @click="selectAll">{{ areAllSelected ? "Deselect All" : "Select All" }}</base-button>
        <div>
          <base-button type="flat" class="tw-w-auto tw-mr-3" @click="hide()">
            Cancel
          </base-button>
          <base-button :loading="isSaving" class="tw-w-auto" @click="save">
            Save
          </base-button>
        </div>
      </div>
    </template>
  </base-slide-over>
</template>

<script>
import { ArrowRightIcon } from "@/assets/svgs";
import { orderBy } from "lodash";

export default {
  components: {
    ArrowRightIcon,
  },
  props: {
    businessId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isLoadingMappings: false,
      isSaving: false,
      businessLocation: {},
      areProgramsLoading: false,
      arePlansLoading: false,
      programs: [],
      plans: [],
      selectedPrograms: [],
      mappings: {},
    };
  },
  computed: {
    isLoading() {
      return this.areProgramsLoading || this.arePlansLoading;
    },
    title() {
      return `${this.businessLocation.name} Mappings`;
    },
    orderedPrograms() {
      return orderBy(this.programs, ["name"], ["asc"]);
    },
    orderedPlans() {
      return orderBy(this.plans, ["name"], ["asc"]);
    },
    areAllSelected() {
      return this.programs.every((l) => !!this.selectedPrograms.find((sl) => sl === l.businessLocationId));
    },
  },
  created() {
    this.getPrograms();
    this.getPlans();
  },
  methods: {
    show(businessLocation) {
      this.businessLocation = businessLocation;
      this.$refs.slideover.show();
      this.selectedPrograms = [];
      this.getMappings();
    },
    hide() {
      this.$refs.slideover.hide();
    },
    async getPrograms() {
      try {
        this.areProgramsLoading = true;
        const response = await this.$axios.get(`/businesses/${this.businessId}/programs/list?includeDeleted=true`);
        this.programs = response.data;
      } finally {
        this.areProgramsLoading = false;
      }
    },
    async getMappings() {
      try {
        this.isLoadingMappings = true;
        const response = await this.$axios.get(`/businesses/${this.businessId}/locations/${this.businessLocation.businessLocationId}/mappings`);
        this.mappings = {};
        response.data.forEach((m) => {
          this.mappings[m.businessProgramId] = m.businessPlanId;
        });
        this.$forceUpdate();
      } finally {
        this.isLoadingMappings = false;
      }
    },
    async getPlans() {
      try {
        this.arePlansLoading = true;
        const response = await this.$axios.get(`/businesses/${this.businessId}/plans/list`);
        this.plans = response.data;
      } finally {
        this.arePlansLoading = false;
      }
    },
    async save() {
      try {
        this.isSaving = true;
        await this.$axios.post(`/businesses/${this.businessId}/locations/${this.businessLocation.businessLocationId}/mappings`, {
          mappings: this.mappings,
        });
        this.hide();
        this.selectedPrograms = [];
        this.mappings = {};
      } catch (error) {
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Unable to save mappings",
          message: `We were not able to save the mappings for this location. There may have been a connection error. Please reload the page to try again.`,
          error,
        });
        throw error;
      } finally {
        this.isSaving = false;
      }
    },
    handleInput(businessProgramId, businessPlanId) {
      if (this.selectedPrograms.includes(businessProgramId)) {
        this.selectedPrograms.forEach((programId) => {
          this.mappings[programId] = businessPlanId;
        });
      } else {
        this.mappings[businessProgramId] = businessPlanId;
      }
      this.$forceUpdate();
    },
    selectAll() {
      if (this.areAllSelected) {
        this.selectedPrograms = [];
      } else {
        this.selectedPrograms = this.programs.map((l) => l.businessProgramId);
      }
    },
  },
};
</script>
